import type { MutableRefObject, ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export interface RegisteredTour {
  id: string;
  showInGlobalHelp: boolean;
  anchorRef: MutableRefObject<HTMLElement | null>;
  hasCompletedTour: boolean;
  startTour: () => void;
}

interface TourRegistryValue {
  tours: RegisteredTour[];
  registerTour: (tour: RegisteredTour) => () => void;
}

const TourRegistryContext = createContext<TourRegistryValue | null>(null);

export const TourRegistryProvider = ({ children }: { children: ReactNode }) => {
  const [toursById, setToursById] = useState(
    () => new Map<string, RegisteredTour>(),
  );

  const registerTour = useCallback((tour: RegisteredTour) => {
    setToursById((current) => new Map(current).set(tour.id, tour));

    return () => {
      setToursById((current) => {
        const next = new Map(current);
        next.delete(tour.id);
        return next;
      });
    };
  }, []);

  const value = useMemo(
    () => ({ tours: [...toursById.values()], registerTour }),
    [registerTour, toursById],
  );

  return (
    <TourRegistryContext.Provider value={value}>
      {children}
    </TourRegistryContext.Provider>
  );
};

export const useTourRegistry = () => useContext(TourRegistryContext);
